import { interpretarFrequencia, type Estudo } from '@sheafyield/dominio';
import { gravar, type Banco } from '@sheafyield/sync';
import { useEffect, useId, useRef, useState } from 'react';
import type { DonoDasLinhas } from '../dados/dono.js';
import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { Folha } from '../ds/Folha.js';
import { Toast } from '../ds/Toast.js';
import {
  gravarRascunhoDaFicha,
  lerRascunhoDaFicha,
  limparRascunhoDaFicha,
  lerFolhaAtivaDaFicha,
  marcarFolhaAtivaDaFicha,
  type CamposDoEstudo,
} from './rascunhoDaFicha.js';
import './pessoas.css';

type Props = {
  banco: Banco;
  dono: DonoDasLinhas;
  aberta: boolean;
  pessoaId: string;
  estudo: Estudo | null;
  aoFechar: () => void;
};
type Campos = Pick<
  Estudo,
  | 'publicacao'
  | 'licao'
  | 'dia_da_semana'
  | 'hora'
  | 'iniciado_em'
  | 'encerrado_em'
>;
type Alteracao = {
  anterior: Estudo | null;
  salva: Estudo;
  campos: (keyof Campos)[];
  mensagem: string;
};
const dias = [
  'domingo',
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
];
const mensagemDe = (causa: unknown) =>
  causa instanceof Error
    ? causa.message
    : 'Não deu para salvar. Tente novamente.';
function dataLocal(em: string) {
  const d = new Date(em);
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function seloDoEstudo(estudo: Pick<Estudo, 'encerrado_em'>): string {
  return estudo.encerrado_em === null
    ? 'Em andamento'
    : `Encerrado em ${new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(new Date(estudo.encerrado_em))}`;
}
function FormularioEstudo({
  estudo,
  identidade,
  encerradoEm,
  erroDeDesfazer,
  aoNovaAcao,
  aoSalvar,
  aoAlternar,
  aoFechar,
}: {
  estudo: Estudo | null;
  identidade: { id: string; user_id: string };
  encerradoEm: string | null;
  erroDeDesfazer: string | null;
  aoNovaAcao: () => void;
  aoSalvar: (valores: Partial<Campos>) => Promise<void>;
  aoAlternar: () => Promise<void>;
  aoFechar: () => void;
}) {
  const [rascunho, setRascunho] = useState(() => {
    const salvo = lerRascunhoDaFicha(identidade, 'estudo');
    if (salvo?.tipo === 'estudo') return salvo;
    const iniciadoOriginal = estudo?.iniciado_em ?? new Date().toISOString();
    const valores: CamposDoEstudo = {
      publicacao: estudo?.publicacao ?? '',
      licao: estudo?.licao ?? '',
      frequencia:
        estudo?.dia_da_semana == null
          ? ''
          : `${dias[estudo.dia_da_semana]}${estudo.hora === null ? '' : ` ${estudo.hora}`}`,
      data: dataLocal(iniciadoOriginal),
    };
    return {
      tipo: 'estudo' as const,
      valores,
      iniciais: valores,
      iniciadoOriginal,
    };
  });
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const emCurso = useRef(false);
  const formulario = useRef<HTMLFormElement>(null);
  const previaId = useId();
  const texto = rascunho.valores.frequencia;
  const frequencia = interpretarFrequencia(texto);
  function mudar(campo: keyof CamposDoEstudo, valor: string) {
    aoNovaAcao();
    const proximo = {
      ...rascunho,
      valores: { ...rascunho.valores, [campo]: valor },
    };
    setRascunho(proximo);
    gravarRascunhoDaFicha(identidade, proximo);
    setErro(null);
  }
  async function executar(acao: () => Promise<void>) {
    if (emCurso.current) return;
    // Uma recusa anterior de Desfazer não pode esconder a validação desta
    // tentativa. Os erros continuam no modal, mas pertencem à ação atual.
    aoNovaAcao();
    emCurso.current = true;
    setOcupado(true);
    setErro(null);
    try {
      await acao();
    } catch (causa) {
      setErro(mensagemDe(causa));
    } finally {
      emCurso.current = false;
      setOcupado(false);
    }
  }
  async function salvar() {
    const valores: Partial<Campos> = {};
    const mudou = (campo: keyof CamposDoEstudo) =>
      estudo === null || rascunho.valores[campo] !== rascunho.iniciais[campo];
    if (mudou('publicacao')) valores.publicacao = rascunho.valores.publicacao;
    if (mudou('licao')) valores.licao = rascunho.valores.licao;
    // Vazio é sem horário fixo. Texto que não se entende é erro, jamais
    // nulos silenciosos. Recorrência intocada conserva até dados parciais.
    if (mudou('frequencia')) {
      if (texto.trim() !== '' && frequencia === null)
        throw new Error(
          'Não entendi a frequência. Tente “sábado 10h” ou “terça 19:30”.',
        );
      valores.dia_da_semana = frequencia?.dia_da_semana ?? null;
      valores.hora = frequencia?.hora ?? null;
    }
    if (mudou('data')) {
      const data = rascunho.valores.data;
      const instante = new Date(`${data}T12:00:00`);
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(data) ||
        Number.isNaN(instante.getTime()) ||
        dataLocal(instante.toISOString()) !== data
      )
        throw new Error('Confira a data de início.');
      // A data original contém hora e milissegundos: editar outro campo
      // não pode arredondar esse instante para a meia-noite.
      valores.iniciado_em =
        data === rascunho.iniciais.data
          ? rascunho.iniciadoOriginal
          : instante.toISOString();
    }
    await aoSalvar(valores);
    limparRascunhoDaFicha(identidade, 'estudo');
  }
  return (
    <Folha
      aberta
      titulo="Estudo bíblico"
      focoInicial={formulario}
      aoFechar={() => {
        if (emCurso.current) return false;
        return aoFechar();
      }}
    >
      <form
        ref={formulario}
        tabIndex={-1}
        className="ficha__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          void executar(salvar);
        }}
      >
        <p className="ficha__estado">
          {seloDoEstudo({ encerrado_em: encerradoEm })}
        </p>
        <Campo
          rotulo="Publicação"
          value={rascunho.valores.publicacao}
          disabled={ocupado}
          onChange={(e) => mudar('publicacao', e.target.value)}
        />
        <Campo
          rotulo="Lição"
          value={rascunho.valores.licao}
          disabled={ocupado}
          onChange={(e) => mudar('licao', e.target.value)}
        />
        <Campo
          rotulo="Frequência"
          placeholder="sábado 10h"
          value={texto}
          disabled={ocupado}
          aria-describedby={previaId}
          onChange={(e) => mudar('frequencia', e.target.value)}
        />
        <p id={previaId} role="status" className="ficha__previa">
          {texto.trim() === ''
            ? 'Sem horário fixo'
            : (frequencia?.descricao ?? 'Informe um dia e um horário válidos.')}
        </p>
        <Campo
          rotulo="Iniciado em"
          type="date"
          value={rascunho.valores.data}
          disabled={ocupado}
          onChange={(e) => mudar('data', e.target.value)}
        />
        <p>Conta no mês quando houver ao menos uma visita de estudo.</p>
        {/* A folha modal torna o restante da ficha inerte: o erro de
            Desfazer precisa estar aqui enquanto o diálogo estiver aberto. */}
        {erroDeDesfazer === null && erro === null ? null : (
          <p role="alert">{erroDeDesfazer ?? erro}</p>
        )}
        <Botao type="submit" largura="cheia" disabled={ocupado}>
          {ocupado ? 'Salvando…' : 'Salvar estudo'}
        </Botao>
        {estudo === null ? null : (
          <Botao
            variante="secundario"
            largura="cheia"
            disabled={ocupado}
            onClick={() => {
              void executar(aoAlternar);
            }}
          >
            {encerradoEm === null ? 'Encerrar estudo' : 'Reabrir estudo'}
          </Botao>
        )}
      </form>
    </Folha>
  );
}

/** O invólucro sobrevive ao fechamento; o Toast continua disponível na
 * ficha. O formulário remonta por abertura/conta/pessoa/estudo, não por sync. */
export function FolhaEstudo({
  banco,
  dono,
  aberta,
  pessoaId,
  estudo,
  aoFechar,
}: Props) {
  const id = estudo?.id ?? 'novo';
  const donoDoEstudo = estudo?.user_id;
  const [conta, setConta] = useState<{ id: string; user_id: string } | null>(
    null,
  );
  const [retomada, setRetomada] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const [aviso, setAviso] = useState<Alteracao | null>(null);
  const [encerradoEm, setEncerradoEm] = useState(estudo?.encerrado_em ?? null);
  const [ultimoEncerramento, setUltimoEncerramento] = useState(
    estudo?.encerrado_em ?? null,
  );
  const encerramentoDaProp = estudo?.encerrado_em ?? null;
  // O resultado local responde mesmo com prop constante; uma nova leitura
  // da ficha também precisa atualizar o selo ao reabrir o mesmo estudo.
  if (ultimoEncerramento !== encerramentoDaProp) {
    setUltimoEncerramento(encerramentoDaProp);
    setEncerradoEm(encerramentoDaProp);
  }
  const desfazendo = useRef(false);
  // O prazo vale só até a primeira tentativa. Limpar a mensagem de erro
  // para editar outro campo não pode tirar a segunda chance de Desfazer.
  const [avisoTentado, setAvisoTentado] = useState<typeof aviso>(null);
  const [origem, setOrigem] = useState({
    id,
    pessoaId,
    dono,
    donoDoEstudo,
    tentativa,
  });
  if (
    origem.id !== id ||
    origem.pessoaId !== pessoaId ||
    origem.dono !== dono ||
    origem.donoDoEstudo !== donoDoEstudo ||
    origem.tentativa !== tentativa
  ) {
    setOrigem({ id, pessoaId, dono, donoDoEstudo, tentativa });
    setConta(null);
    setRetomada(false);
    setErro(null);
    setAviso(null);
    setAvisoTentado(null);
    setEncerradoEm(estudo?.encerrado_em ?? null);
  }
  useEffect(() => {
    let ativa = true;
    void dono()
      .then((user_id) => {
        if (!ativa) return;
        if (donoDoEstudo !== undefined && donoDoEstudo !== user_id)
          throw new Error('Este estudo pertence a outra conta.');
        setConta({ id: `${pessoaId}:${id}`, user_id });
        setRetomada(
          lerFolhaAtivaDaFicha({ id: pessoaId, user_id }) === `estudo:${id}`,
        );
      })
      .catch((causa: unknown) => {
        if (ativa) setErro(mensagemDe(causa));
      });
    return () => {
      ativa = false;
    };
  }, [dono, id, pessoaId, donoDoEstudo, tentativa]);
  useEffect(() => {
    if ((aberta || retomada) && conta !== null)
      marcarFolhaAtivaDaFicha(
        { id: pessoaId, user_id: conta.user_id },
        `estudo:${id}`,
      );
  }, [aberta, retomada, conta, pessoaId, id]);
  function fechar() {
    if (conta !== null)
      marcarFolhaAtivaDaFicha({ id: pessoaId, user_id: conta.user_id }, null);
    setRetomada(false);
    aoFechar();
  }
  async function conferir(user_id: string) {
    const pessoa = await banco.pessoa.get(pessoaId);
    if (pessoa === undefined || pessoa.deleted)
      throw new Error('Esta ficha não está mais disponível.');
    if (pessoa.user_id !== user_id)
      throw new Error('Esta ficha pertence a outra conta.');
  }
  function conferirEstudo(
    atual: Estudo | undefined,
    user_id: string,
  ): asserts atual is Estudo {
    if (atual === undefined || atual.deleted)
      throw new Error('Este estudo não está mais disponível.');
    if (atual.user_id !== user_id)
      throw new Error('Este estudo pertence a outra conta.');
    if (atual.pessoa_id !== pessoaId)
      throw new Error('Este estudo pertence a outra ficha.');
  }
  function conferirDatas(
    entrada: Pick<Estudo, 'iniciado_em' | 'encerrado_em'>,
  ) {
    if (
      entrada.encerrado_em !== null &&
      Date.parse(entrada.iniciado_em) > Date.parse(entrada.encerrado_em)
    )
      throw new Error('A data de início precisa ser anterior ao encerramento.');
  }
  async function escrever(
    valores: Partial<Campos>,
    acao: 'salvar' | 'encerrar' | 'reabrir' = 'salvar',
  ) {
    const user_id = await dono();
    if (conta === null || user_id !== conta.user_id)
      throw new Error('A conta mudou. Abra o formulário novamente.');
    const resultado = await banco.transaction(
      'rw',
      banco.pessoa,
      banco.estudo,
      banco.fila,
      async () => {
        await conferir(user_id);
        const anterior =
          estudo === null ? null : await banco.estudo.get(estudo.id);
        if (anterior !== null) conferirEstudo(anterior, user_id);
        // A intenção vem do botão tocado, antes de aguardar dono/banco.
        // Inverter o estado relido faria “Encerrar” reabrir após um sync.
        // Se já encerrou, conservamos inclusive o carimbo recebido.
        const novos =
          acao === 'salvar'
            ? valores
            : {
                encerrado_em:
                  acao === 'encerrar'
                    ? (anterior?.encerrado_em ?? new Date().toISOString())
                    : null,
              };
        const campos = (Object.keys(novos) as (keyof Campos)[]).filter(
          (campo) => anterior === null || anterior[campo] !== novos[campo],
        );
        if (anterior !== null && campos.length === 0)
          return { alteracao: null, encerradoEm: anterior.encerrado_em };
        const entrada =
          anterior === null
            ? {
                id: crypto.randomUUID(),
                user_id,
                pessoa_id: pessoaId,
                publicacao: '',
                licao: '',
                dia_da_semana: null,
                hora: null,
                iniciado_em: new Date().toISOString(),
                encerrado_em: null,
                ...novos,
              }
            : { ...anterior, ...novos };
        conferirDatas(entrada);
        const salva = await gravar(banco, 'estudo', entrada);
        return {
          alteracao: {
            anterior,
            salva,
            campos,
            mensagem:
              acao === 'salvar'
                ? 'Estudo salvo'
                : acao === 'encerrar'
                  ? 'Estudo encerrado'
                  : 'Estudo reaberto',
          },
          encerradoEm: salva.encerrado_em,
        };
      },
    );
    setAviso(resultado.alteracao);
    setAvisoTentado(null);
    setErro(null);
    // Mesmo sem escrita, a leitura confirmou o estado recebido do sync.
    setEncerradoEm(resultado.encerradoEm);
    if (acao === 'salvar') fechar();
  }
  async function desfazer() {
    if (aviso === null || desfazendo.current) return;
    desfazendo.current = true;
    setAvisoTentado(aviso);
    try {
      const user_id = await dono();
      if (user_id !== aviso.salva.user_id)
        throw new Error('A conta mudou. Não foi possível desfazer.');
      const restaurado = await banco.transaction(
        'rw',
        banco.pessoa,
        banco.estudo,
        banco.fila,
        async () => {
          await conferir(user_id);
          const atual = await banco.estudo.get(aviso.salva.id);
          conferirEstudo(atual, user_id);
          if (aviso.anterior === null) {
            if (atual.updated_at !== aviso.salva.updated_at)
              throw new Error(
                'O estudo foi alterado depois de salvar. Não foi possível desfazer a criação.',
              );
            return gravar(banco, 'estudo', { ...atual, deleted: true });
          }
          // Restaurar só campos que ainda têm nosso valor preserva o sync.
          const campos = aviso.campos.filter(
            (campo) => atual[campo] === aviso.salva[campo],
          );
          if (campos.length === 0)
            throw new Error(
              'O estudo foi alterado depois de salvar. Não foi possível desfazer.',
            );
          const anterior = aviso.anterior;
          const entrada = {
            ...atual,
            ...Object.fromEntries(
              campos.map((campo) => [campo, anterior[campo]]),
            ),
          };
          conferirDatas(entrada);
          return gravar(banco, 'estudo', entrada);
        },
      );
      setEncerradoEm(restaurado.encerrado_em);
      setAviso(null);
      setAvisoTentado(null);
      setErro(null);
    } catch (causa) {
      setErro(mensagemDe(causa));
    } finally {
      desfazendo.current = false;
    }
  }
  const formularioAberto = (aberta || retomada) && conta !== null;
  return (
    <>
      {formularioAberto ? (
        <FormularioEstudo
          key={`${conta.user_id}:${conta.id}`}
          estudo={estudo}
          identidade={conta}
          encerradoEm={encerradoEm}
          erroDeDesfazer={erro}
          aoNovaAcao={() => setErro(null)}
          aoSalvar={escrever}
          aoAlternar={() =>
            escrever({}, encerradoEm === null ? 'encerrar' : 'reabrir')
          }
          aoFechar={fechar}
        />
      ) : null}
      {aberta && conta === null && erro === null ? (
        <p role="status">Abrindo formulário…</p>
      ) : null}
      {erro === null ||
      formularioAberto ||
      (!aberta && !retomada && aviso === null) ? null : (
        <div>
          <p role="alert">{erro}</p>
          {conta === null ? (
            <Botao
              variante="secundario"
              onClick={() => setTentativa((n) => n + 1)}
            >
              Tentar novamente
            </Botao>
          ) : null}
        </div>
      )}
      <Toast
        chave={aviso}
        segundos={aviso !== null && avisoTentado === aviso ? 0 : 6}
        mensagem={aviso?.mensagem ?? null}
        {...(aviso === null
          ? {}
          : {
              acao: {
                rotulo: 'Desfazer',
                aoAcionar: () => {
                  void desfazer();
                },
              },
            })}
        aoDispensar={() => {
          setAviso(null);
          setAvisoTentado(null);
          setErro(null);
        }}
      />
    </>
  );
}
